// AnalyticsLayouts — 4 layout variants (cards/twocol/table/hybrid).
// Port of design/v3/AnalyticsLayouts.jsx layout switching only —
// the page chrome (TopNav, sidebar) is owned by the route.

import { useEffect, useState } from 'react';
import {
  KpiRow,
  DailyRevenuePanel,
  HourlyPanel,
  PlatformPanel,
  StoreListPanel,
  MenuRankingPanel,
  ToppingsPanel,
  AnomaliesPanel,
  KeywordsPanel,
  InventoryUsagePanel,
} from './panels';
import type { AnalyticsScope } from './queries';

export type Variant = 'cards' | 'twocol' | 'table' | 'hybrid';

const STORAGE_KEY = 'dvvb.analytics.v3.state';

interface SavedState {
  variant?: Variant;
}

function loadSaved(): SavedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const v = JSON.parse(raw) as SavedState;
    return v;
  } catch {
    return {};
  }
}

function saveState(s: SavedState) {
  try {
    const prev = loadSaved();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prev, ...s }));
  } catch {
    /* noop */
  }
}

export function useLayoutVariant() {
  const [variant, setVariantState] = useState<Variant>(
    () => (loadSaved().variant as Variant) ?? 'cards',
  );
  useEffect(() => {
    saveState({ variant });
  }, [variant]);
  return [variant, setVariantState] as const;
}

export interface VariantPickerProps {
  variant: Variant;
  onChange: (v: Variant) => void;
}

const VARIANTS: Array<{ id: Variant; label: string }> = [
  { id: 'cards', label: '카드형' },
  { id: 'twocol', label: '2단' },
  { id: 'table', label: '테이블형' },
  { id: 'hybrid', label: '하이브리드' },
];

export function VariantPicker({ variant, onChange }: VariantPickerProps) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        background: 'var(--bg-muted)',
        padding: 3,
        borderRadius: 8,
        flexShrink: 0,
      }}
    >
      {VARIANTS.map((l) => {
        const active = variant === l.id;
        return (
          <span
            key={l.id}
            onClick={() => onChange(l.id)}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              background: active ? 'var(--bg-surface)' : 'transparent',
              color: active ? 'var(--fg-1)' : 'var(--fg-3)',
              border: active
                ? '1px solid var(--border-base)'
                : '1px solid transparent',
              whiteSpace: 'nowrap',
            }}
          >
            {l.label}
          </span>
        );
      })}
    </div>
  );
}

interface LayoutProps {
  scope: AnalyticsScope;
  // Phase 5b-3 — 드릴다운: panel row 클릭 시 selection 갱신.
  onSelectPlatform?: (platformId: string) => void;
  onSelectStore?: (storeId: string, platformId?: string) => void;
  selectedPlatformId?: string | null;
  selectedStoreId?: string | null;
}

export function AnalyticsLayout({
  variant,
  scope,
  onSelectPlatform,
  onSelectStore,
  selectedPlatformId,
  selectedStoreId,
}: LayoutProps & { variant: Variant }) {
  const lp: LayoutProps = {
    scope,
    onSelectPlatform,
    onSelectStore,
    selectedPlatformId,
    selectedStoreId,
  };
  if (variant === 'table') return <LayoutTable {...lp} />;
  if (variant === 'twocol') return <LayoutTwoCol {...lp} />;
  if (variant === 'hybrid') return <LayoutHybrid {...lp} />;
  return <LayoutCards {...lp} />;
}

// Responsive gap — 시안 design/v3/AnalyticsLayouts.jsx 의 `isMobile ? 12 : 16` 룰.
// CSS 변수 + tokens.css 의 (max-width: 767px) media query 로 mobile 12 / 그 외 16.
const gap = 'var(--analytics-gap)';

function LayoutCards({
  scope,
  onSelectPlatform,
  onSelectStore,
  selectedPlatformId,
  selectedStoreId,
}: LayoutProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      <KpiRow scope={scope} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr 1fr',
          gap,
        }}
      >
        <DailyRevenuePanel scope={scope} />
        <PlatformPanel
          scope={scope}
          onSelectPlatform={onSelectPlatform}
          selectedPlatformId={selectedPlatformId}
        />
        <HourlyPanel scope={scope} />
      </div>
      <StoreListPanel
        scope={scope}
        onSelectStore={onSelectStore}
        selectedStoreId={selectedStoreId}
      />
      <MenuRankingPanel scope={scope} limit={10} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap,
        }}
      >
        <AnomaliesPanel scope={scope} />
        <InventoryUsagePanel scope={scope} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap,
        }}
      >
        <ToppingsPanel scope={scope} />
        <KeywordsPanel scope={scope} />
      </div>
    </div>
  );
}

function LayoutTwoCol({
  scope,
  onSelectPlatform,
  onSelectStore,
  selectedPlatformId,
  selectedStoreId,
}: LayoutProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      <KpiRow scope={scope} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>
          <DailyRevenuePanel scope={scope} />
          <PlatformPanel
            scope={scope}
            onSelectPlatform={onSelectPlatform}
            selectedPlatformId={selectedPlatformId}
          />
          <StoreListPanel
            scope={scope}
            onSelectStore={onSelectStore}
            selectedStoreId={selectedStoreId}
          />
          <KeywordsPanel scope={scope} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>
          <HourlyPanel scope={scope} />
          <MenuRankingPanel scope={scope} limit={10} />
          <AnomaliesPanel scope={scope} />
          <InventoryUsagePanel scope={scope} />
        </div>
      </div>
      <ToppingsPanel scope={scope} />
    </div>
  );
}

function LayoutTable({
  scope,
  onSelectPlatform,
  onSelectStore,
  selectedPlatformId,
  selectedStoreId,
}: LayoutProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      <KpiRow scope={scope} />
      <MenuRankingPanel scope={scope} />
      <StoreListPanel
        scope={scope}
        onSelectStore={onSelectStore}
        selectedStoreId={selectedStoreId}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap,
        }}
      >
        <PlatformPanel
          scope={scope}
          onSelectPlatform={onSelectPlatform}
          selectedPlatformId={selectedPlatformId}
        />
        <HourlyPanel scope={scope} />
      </div>
      <DailyRevenuePanel scope={scope} />
      <InventoryUsagePanel scope={scope} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap,
        }}
      >
        <AnomaliesPanel scope={scope} />
        <KeywordsPanel scope={scope} />
      </div>
      <ToppingsPanel scope={scope} />
    </div>
  );
}

function LayoutHybrid({
  scope,
  onSelectPlatform,
  onSelectStore,
  selectedPlatformId,
  selectedStoreId,
}: LayoutProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.6fr 1fr',
          gap,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>
          <KpiRow scope={scope} />
          <DailyRevenuePanel scope={scope} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>
          <PlatformPanel
            scope={scope}
            onSelectPlatform={onSelectPlatform}
            selectedPlatformId={selectedPlatformId}
          />
          <HourlyPanel scope={scope} />
        </div>
      </div>
      <MenuRankingPanel scope={scope} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.3fr 1fr',
          gap,
        }}
      >
        <StoreListPanel
          scope={scope}
          onSelectStore={onSelectStore}
          selectedStoreId={selectedStoreId}
        />
        <AnomaliesPanel scope={scope} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap,
        }}
      >
        <InventoryUsagePanel scope={scope} />
        <ToppingsPanel scope={scope} />
        <KeywordsPanel scope={scope} />
      </div>
    </div>
  );
}
